import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { INVOICE_TYPE_CLIENT, InvoiceDto, InvoiceReportRowDto } from '@sr/dto';
import { LinkToInvoice } from 'entities/client-invoices-list/ui/link-to-invoice';
import { LinkToPurchase } from 'shared/ui/links/link-to-purchase';
import { LinkToMedia } from 'shared/ui/links/link-to-media';
import { ButtonLink } from 'shared/ui/link-base';
import { formatDatesAndPeriod } from 'utils/date-format-helpers';
import { purchaseLinkManager } from 'utils/link-manager';
import PriceSpan from 'shared/ui/price-span';
import { ReactNode } from 'react';
import { ProfitabilityBadge } from './profitability-badge';

export type InvoiceReportTotals = {
  buyerRowsTotal: number,
  sellerrRowsTotal?: number,

  buyerPartialPaidTotal?: number,
  sellerPartialPaidTotal?: number,

  totalProfitByPayments?: number,
  totalProfitByPurchases: number,
}

type Props = {
  invoice: InvoiceDto
  rows: InvoiceReportRowDto[]
  totals: InvoiceReportTotals
}

const getReportColumns = (showProfit: boolean) => showProfit
  ? 'minmax(280px, 2fr) 90px minmax(140px, 1.2fr) repeat(2, minmax(120px, 1fr)) minmax(150px, 1.3fr)'
  : 'minmax(280px, 2fr) 90px minmax(140px, 1.2fr) repeat(2, minmax(120px, 1fr))';

export const InvoiceReportRows = ({ rows, totals, invoice }: Props) => {
  const isClientInvoice = invoice.type === INVOICE_TYPE_CLIENT;

  if (rows.length === 0) {
    return (
      <Paper elevation={0}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          Нет заявок
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper elevation={0}>
      <Box sx={{ overflowX: 'auto' }} tabIndex={0} role="region" aria-label="Услуги: доходы и расходы">
        <Stack spacing={1} sx={{ minWidth: isClientInvoice ? 1000 : 840, px: 1, pb: 1 }}>
          <ReportHeader showProfit={isClientInvoice} />
          {rows.map((row, index) => (
            <ReportRow key={row.buyerRow.id} row={row} number={index + 1} isClientInvoice={isClientInvoice} />
          ))}
          <Divider />
          <FooterRows totals={totals} isClientInvoice={isClientInvoice} />
        </Stack>
      </Box>
    </Paper>
  );
};

const ReportHeader = ({ showProfit }: { showProfit: boolean }) => (
  <Box sx={{ border: '1px solid transparent' }}>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: getReportColumns(showProfit),
        alignItems: 'center',
        columnGap: 1.25,
        borderLeft: '3px solid transparent',
        px: 1,
        py: 0.75
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" aria-label="Обозначения цветов">
        <ColorDefinition color="success.main" label="Доход" />
        <ColorDefinition color="warning.main" label="Расход" />
        <ColorDefinition color="primary.main" label="Связанная заявка" />
      </Stack>
      <Typography variant="caption" color="text.secondary">Количество</Typography>
      <Typography variant="caption" color="text.secondary">Период</Typography>
      <Typography variant="caption" color="text.secondary" align="right">Стоимость</Typography>
      <Typography variant="caption" color="text.secondary" align="right">Оплаты</Typography>
      {showProfit && <Typography variant="caption" color="text.secondary" align="right">Прибыль</Typography>}
    </Box>
  </Box>
);

const ColorDefinition = ({ color, label }: { color: string, label: string }) => (
  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Stack>
);

const ReportRow = ({ isClientInvoice, row, number }: { isClientInvoice: boolean, row: InvoiceReportRowDto, number: number }) => {
  const linkedPurchase = row.purchase;
  const buyerRow = row.buyerRow;
  const sellerRow = row.sellerRow;
  const sellerInvoice = row.sellerInvoice;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <PurchaseHeader purchase={linkedPurchase} number={number} />
      <ReportLine
        title="Доход"
        description={
          <>
            {buyerRow.description}
            {buyerRow.media && <> <LinkToMedia media={buyerRow.media} /></>}
          </>
        }
        amount={`${buyerRow.amount} шт`}
        period={formatDatesAndPeriod(buyerRow.startDate, buyerRow.endDate)}
        price={<PriceSpan size='small' price={buyerRow.priceDiscount} />}
        paid={<PriceSpan size='small' price={buyerRow.paidAmount ?? 0} isApproximate showZeroKop />}
        profit={isClientInvoice ? <ProfitWithProfitabilty price={row.buyerRow.priceDiscount} profit={row.profitByPurchases} alignRight /> : undefined}
      />

      <ReportLine
        title="Расход"
        expense
        description={sellerRow
          ? <>
            {sellerRow.description}
            {sellerInvoice && <> (Счет: <LinkToInvoice invoice={sellerInvoice} />)</>}
          </>
          : '-'}
        amount={sellerRow ? `${sellerRow.amount} шт` : '-'}
        period={sellerRow ? formatDatesAndPeriod(sellerRow.startDate, sellerRow.endDate) : '-'}
        price={sellerRow ? <PriceSpan size='small' price={sellerRow.priceDiscount} /> : '-'}
        paid={sellerRow ? <PriceSpan isApproximate size='small' price={sellerRow.paidAmount ?? 0} showZeroKop /> : '-'}
        showProfit={isClientInvoice}
      />
    </Box>
  );
};

const PurchaseHeader = ({ purchase, number }: { purchase: InvoiceReportRowDto['purchase'], number: number }) => (
  <Box
    sx={{
      alignItems: 'center',
      bgcolor: 'action.hover',
      borderBottom: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      justifyContent: 'space-between',
      px: 1,
      py: 0.5
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        component="span"
        aria-label={`Строка ${number}`}
        sx={{ bgcolor: 'action.selected', borderRadius: 0.5, px: 0.75, py: 0.25, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
      >
        {number}
      </Typography>
      {purchase
        ? (
          <Typography variant="body2" component="div" fontWeight={500}>
            <LinkToPurchase purchase={purchase}>
              Заявка №{purchase.id}
            </LinkToPurchase>
          </Typography>
        )
        : (
          <Typography variant="body2" color="text.secondary">
            Без заявки
          </Typography>
        )}
    </Stack>

    {purchase && (
      <ButtonLink
        to={purchaseLinkManager.link(purchase.id)}
        buttonProps={{
          color: 'primary',
          size: 'small',
          startIcon: <LockOpenIcon />,
          sx: { py: 0, minHeight: 26, whiteSpace: 'nowrap' },
          variant: 'text'
        }}
      >
        Открыть
      </ButtonLink>
    )}
  </Box>
);

const FooterRows = ({ totals, isClientInvoice }: { totals: InvoiceReportTotals, isClientInvoice: boolean }) => {
  return (
    <Box sx={{ bgcolor: 'action.hover', border: '1px solid transparent' }}>
      <Stack spacing={0.75}>
        <TotalLine
          label="Итого доходы"
          color="success.main"
          price={<PriceSpan size="small" price={totals.buyerRowsTotal} showZeroKop />}
          paid={<PriceSpan size="small" price={totals.buyerPartialPaidTotal ?? 0} showZeroKop />}
          profit={isClientInvoice ? <ProfitWithProfitabilty price={totals.buyerRowsTotal} profit={totals.totalProfitByPurchases} alignRight /> : undefined}
        />
        <TotalLine
          label="Итого расходы"
          color="warning.main"
          price={<PriceSpan size="small" price={totals.sellerrRowsTotal ?? 0} showZeroKop />}
          paid={<PriceSpan size="small" price={totals.sellerPartialPaidTotal ?? 0} showZeroKop />}
          showProfit={isClientInvoice}
        />
      </Stack>
    </Box>
  );
};


const isEmptyNode = (value: ReactNode) =>
  value === null || value === undefined || value === false || value === '';

const ReportLine = ({
  title,
  description,
  amount,
  period,
  price,
  paid,
  profit,
  expense = false,
  showProfit = profit !== undefined
}: {
  title: string,
  description: ReactNode,
  amount: ReactNode,
  period: ReactNode,
  price: ReactNode,
  paid: ReactNode,
  profit?: ReactNode,
  expense?: boolean,
  showProfit?: boolean,
}) => (
  <Box
    sx={{
      bgcolor: theme => alpha(expense ? theme.palette.warning.main : theme.palette.success.main, 0.04),
      borderTop: expense ? '1px solid' : 0,
      borderColor: 'divider',
      borderLeft: '3px solid',
      borderLeftColor: expense ? 'warning.main' : 'success.main',
      display: 'grid',
      alignItems: 'center',
      columnGap: 1.25,
      gridTemplateColumns: getReportColumns(showProfit),
      px: 1,
      py: 0.75
    }}
  >
    <ReportField
      label={<Box component="span" sx={{ color: expense ? 'warning.dark' : 'success.dark', fontWeight: 700 }}>{title}</Box>}
      value={description}
    />
    <ReportField label="Количество" value={amount} hideLabel />
    <ReportField label="Период" value={period} hideLabel />
    <ReportField label="Стоимость" value={price} align="right" hideLabel />
    <ReportField label="Оплаты" value={paid} align="right" hideLabel />
    {showProfit && <ReportField label="Прибыль" value={profit} align="right" hideLabel />}
  </Box>
);

const TotalLine = ({
  label,
  color,
  price,
  paid,
  profit,
  showProfit = profit !== undefined
}: {
  label: string,
  color: string,
  price: ReactNode,
  paid: ReactNode,
  profit?: ReactNode,
  showProfit?: boolean,
}) => (
  <Box
    sx={{
      alignItems: 'center',
      display: 'grid',
      columnGap: 1.25,
      gridTemplateColumns: getReportColumns(showProfit),
      borderLeft: '3px solid',
      borderColor: color,
      px: 1,
      py: 0.75
    }}
  >
    <Typography variant="body2" fontWeight={600} sx={{ gridColumn: '1 / 4' }}>
      {label}:
    </Typography>
    <ReportField label="Стоимость" value={price} align="right" hideLabel />
    <ReportField label="Оплаты" value={paid} align="right" hideLabel />
    {showProfit && <ReportField label="Прибыль" value={profit} align="right" hideLabel />}
  </Box>
);

const ReportField = ({
  label,
  value,
  align = 'left',
  hideLabel = false
}: {
  label: ReactNode,
  value: ReactNode,
  align?: 'left' | 'right',
  hideLabel?: boolean,
}) => (
  <Box
    sx={{
      minWidth: 0,
      position: 'relative',
      typography: 'body2',
      fontVariantNumeric: 'tabular-nums',
      textAlign: hideLabel ? align : { xs: 'left', lg: align }
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      component="div"
      sx={hideLabel ? { position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 } : undefined}
    >
      {label}
    </Typography>
    <Box sx={{ overflowWrap: 'anywhere' }}>
      {isEmptyNode(value) ? '-' : value}
    </Box>
  </Box>
);

const ProfitWithProfitabilty = ({ price, profit, alignRight = false }: { price?: number, profit: number, alignRight?: boolean }) =>
  <Stack
    direction="row"
    spacing={0.5}
    alignItems="center"
    sx={{ flexWrap: 'wrap', minWidth: 0, rowGap: 0.5, justifyContent: alignRight ? 'flex-end' : { xs: 'flex-start', lg: 'flex-end' } }}
  >
    <div>
      <PriceSpan size="small" price={profit} zeroAsDash showZeroKop coloring />
    </div>
    <div>
      {!!price && !!profit &&
        <ProfitabilityBadge valueInPercent={(100 * (profit / price))} />
      }
    </div>
  </Stack>;
